import { IWeatherDialogContentProps } from '../LeftMenuBar/Weather/WeatherDialogContent';
import React, { createContext, useState } from 'react';
import useSwitch from '../../utils/useSwitch';
import useTheme, { defaultStyle, StyleType } from './Theme/useTheme';
import Event from '../../JSDC/utils/Event';
import { Article } from '../../JSDC/Dguidewalks/proxyParser/@types';
import { ListingCategory } from '../../JSDC/utils/listingCategoryIcon';

// make sure they match menuItem components's props
// these items should be same as DguidewalksApp component content
export const defaultMenuItems = [
  {
    id: '地圖圖層',
    name: '地圖圖層',
  },
  {
    id: '河溪搜尋',
    name: '河溪搜尋',
  },
  {
    id: '環境紀錄',
    name: '環境紀錄',
  },
  {
    id: '氣象預測',
    name: '氣象預測',
  },
  {
    id: '圖例說明',
    name: '圖例說明',
  },
  {
    id: '工具設定',
    name: '工具設定',
  },
  {
    id: '圖臺介紹',
    name: '圖臺介紹',
  },
  {
    id: '關於圖臺',
    name: '關於圖臺',
  },
];

export type WeatherConfig = {
  disabled?: boolean;
  token: string | undefined;
  locations: IWeatherDialogContentProps['locations'];
};

export type LegendConfig = {
  disabled?: boolean;
  activeLegends?: Array<ListingCategory>;
};

export type DuiContextType = {
  sidebarTitle: string;
  sidebarSubtitle: string;
  aboutWalkImgSrc: string;
  aboutWalkContent: string;
  credit: string;
  creditHref?: string;
  headerMBImgSrc: string;
  headerDImgSrc: string;
  activeMenuId: string | undefined;
  menuSwitch: (id: string | undefined) => void;
  menuSwitcherAction: (id: string) => {
    onClick: () => void;
    onClose: () => void;
  };
  menuSwitchEvent: Event<string | undefined>;
  weatherConfig: WeatherConfig;
  legendConfig: LegendConfig;
  onSceneTargetClick: (article: Article) => void;
  onSceneNavigate: (title: string) => void;
  sceneCardsReducer?: (data: Article[]) => Article[];
};

export const initialDuiContext = {};

const DuiContext = createContext<DuiContextType>(initialDuiContext as DuiContextType);

type MenuItemType = {
  id: string;
  name: string;
};

export interface IDuiContextProviderProps {
  children?: React.ReactNode;
  sidebarTitle: string;
  sidebarSubtitle: string;
  aboutWalkImgSrc: string;
  aboutWalkContent: string;
  credit: string;
  creditHref?: string;
  headerMBImgSrc: string;
  headerDImgSrc: string;
  menuSwitchItems: Array<MenuItemType>;
  weatherConfig: WeatherConfig;
  legendConfig: LegendConfig;
  themeConfig?: StyleType;
  onSceneTargetClick?: (article: Article) => void;
  onSceneNavigate?: (title: string) => void;
  sceneCardsReducer?: (data: Article[]) => Article[];
}

const DuiContextProvider: React.FC<IDuiContextProviderProps> = ({
  sidebarTitle,
  sidebarSubtitle,
  aboutWalkImgSrc,
  aboutWalkContent,
  credit,
  creditHref,
  headerMBImgSrc,
  headerDImgSrc,
  children,
  weatherConfig,
  legendConfig,
  menuSwitchItems,
  themeConfig = defaultStyle,
  onSceneTargetClick = () => null,
  onSceneNavigate = () => null,
  sceneCardsReducer,
}) => {
  useTheme(themeConfig);
  const { switchById, activeId } = useSwitch<MenuItemType>([
    ...defaultMenuItems,
    ...menuSwitchItems,
  ]);
  const [menuSwitchEvent] = useState(new Event<string | undefined>());

  const menuSwitch = (id: string | undefined) => {
    switchById(id);
    menuSwitchEvent.raise(id);
  };
  const menuSwitcherAction = (id: string) => {
    return {
      onClick: () => menuSwitch(id),
      onClose: () => {
        switchById(undefined);
        menuSwitchEvent.raise(undefined);
      },
    };
  };

  const value = {
    sidebarTitle,
    sidebarSubtitle,
    aboutWalkImgSrc,
    aboutWalkContent,
    credit,
    creditHref,
    headerMBImgSrc,
    headerDImgSrc,
    activeMenuId: activeId,
    menuSwitch,
    menuSwitcherAction,
    menuSwitchEvent,
    weatherConfig,
    legendConfig,
    onSceneTargetClick,
    onSceneNavigate,
    sceneCardsReducer,
  };
  return <DuiContext.Provider value={value}>{children}</DuiContext.Provider>;
};
DuiContextProvider.displayName = 'DuiContextProvider';
export { DuiContextProvider, DuiContext };
